import AccessibilityIcon from "@mui/icons-material/Accessibility";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import React, { useEffect, useState } from "react";
import { IControlledMutator, IOptions } from "sonobello.utilities.react";
import {
  AutocompleteWithIcons,
  AutocompleteWithIconsOption,
  IAutocompleteWithIconsIconConfiguration,
  IAutocompleteWithIconsProps
} from "sonobello.utilities.react.mui";

import { UserContextProps } from "../../../Context/UserContext";
import { EnhancedCenter } from "../../../Models/Center";
import EnvironmentConfiguration from "../../Constants/EnvironmentConfiguration";

/** A mapping from the environment configured business unit to the icon properties to be rendered in a center option. */
const BusinessUnitIconMap: Record<
  keyof (typeof EnvironmentConfiguration)["businessUnitIds"],
  IAutocompleteWithIconsIconConfiguration
> = {
  contour: {
    Icon: HourglassTopIcon,
    props: { color: "secondary" },
    tooltip: "Contour"
  },
  body: {
    Icon: AccessibilityIcon,
    props: { color: "primary" },
    tooltip: "Body"
  }
};

const configuredBusinessUnitIds = Object.entries(EnvironmentConfiguration.businessUnitIds) as [
  keyof typeof EnvironmentConfiguration.businessUnitIds,
  string
][];

class CenterAutoCompleteWithIconsOption extends AutocompleteWithIconsOption<
  EnhancedCenter,
  typeof BusinessUnitIconMap
> {
  constructor(center: EnhancedCenter) {
    const icons: (keyof typeof BusinessUnitIconMap)[] = [];
    configuredBusinessUnitIds.forEach(([iconName, businessUnitId]) => {
      if (center.businessUnitIds.some(id => id === businessUnitId)) icons.push(iconName);
    });
    super(icons, center.name, center);
  }
}

export interface ICenterSelectorProps
  extends IControlledMutator<EnhancedCenter | null>,
    IOptions<UserContextProps["centers"][0]>,
    Omit<
      IAutocompleteWithIconsProps<EnhancedCenter, typeof BusinessUnitIconMap>,
      "iconConfiguration" | "options" | "value" | "label" | "onChange"
    > {}

/**
 * An extension of {@link AutocompleteWithIcons} which is configured to support {@link EnhancedCenter} options and show
 * the center's supported business units as option icons.
 * @remarks Hides the clear value button.
 */
const CenterSelector: React.FC<ICenterSelectorProps> = ({ options, value, onChange, ...rest }) => {
  const [selectorOptions, setSelectorOptions] = useState<CenterAutoCompleteWithIconsOption[]>([]);
  useEffect(() => {
    const autocompleteOptions: CenterAutoCompleteWithIconsOption[] = [];
    options.forEach(option => {
      if (option.isActive) autocompleteOptions.push(new CenterAutoCompleteWithIconsOption(option));
    });
    setSelectorOptions(autocompleteOptions);
  }, [options]);
  const selectorValue = value ? selectorOptions.find(o => o.value.id === value.id) : null;
  return (
    <AutocompleteWithIcons
      {...rest}
      value={selectorValue || null}
      label="Centers"
      iconConfiguration={BusinessUnitIconMap}
      onChange={selectedOption => onChange(selectedOption && selectedOption.value)}
      options={selectorOptions}
      hideClearButton
    />
  );
};

export default CenterSelector;
